import { FC, PropsWithChildren, RefObject, useCallback, useContext, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import classnames from 'classnames';

import { TableContext } from '@hh.ru/magritte-ui-table/context';
import { Density } from '@hh.ru/magritte-ui-table/types';

import cellStyles from './cell.less';
import styles from './table.less';

interface StickyTableHeaderProps extends PropsWithChildren {
    root: HTMLDivElement;
    headerRef: RefObject<HTMLTableSectionElement>;
    divider?: boolean;
    density: Density;
}

const DENSITY_TO_CSS = {
    'extra-small': styles.densityExtraSmall,
    small: styles.densitySmall,
    medium: styles.densityMedium,
    large: styles.densityLarge,
};

export const StickyTableHeader: FC<StickyTableHeaderProps> = ({ root, divider, density, headerRef, children }) => {
    const ref = useRef<HTMLTableElement>(null);
    const { fontLoaded: renderPortal, subscribeResizeHandler, unsubscribeResizeHandler } = useContext(TableContext);

    const calcWidth = useCallback(() => {
        if (ref.current && root && headerRef.current) {
            const columnsHeader =
                headerRef.current.querySelectorAll<HTMLTableCellElement>('[data-magritte-table-cell]');
            const widths: number[] = [];
            [...columnsHeader].forEach((column, index) => {
                widths[index] = column.getBoundingClientRect().width;
            });
            const columns = ref.current.querySelectorAll<HTMLElement>('[data-magritte-table-cell]');
            [...columns].forEach((column, index) => {
                column.style.setProperty('width', `${widths[index]}px`);
            });
            root.style.setProperty('margin-bottom', `-${headerRef.current.getBoundingClientRect().height}px`);
        }
    }, [headerRef, root]);

    useLayoutEffect(() => {
        if (root && renderPortal) {
            calcWidth();
            subscribeResizeHandler?.(calcWidth, 1);

            return () => unsubscribeResizeHandler?.(calcWidth);
        }

        return void 0;
    }, [root, renderPortal, calcWidth, subscribeResizeHandler, unsubscribeResizeHandler]);

    return (
        <>
            {renderPortal &&
                createPortal(
                    <table ref={ref} className={classnames(styles.table, cellStyles.duplicateTable)}>
                        <thead
                            className={classnames(styles.header, DENSITY_TO_CSS[density], {
                                [styles.withDivider]: !!divider,
                            })}
                            data-qa="table-sticky-header"
                        >
                            {children}
                        </thead>
                    </table>,
                    root
                )}
        </>
    );
};
